// The pure model behind the listing's matched-buyers table: one ROW per matched contact,
// whether it is the realtor's own or a colleague's, with what the viewer may see and do.
//
// Kept free of React so the rules — who is selectable, how a redacted colleague row reads,
// how the scope/tier filters count — are unit-tested (matchRows.test.ts) rather than implied
// by JSX.
import type {
  ListingListedFor,
  ListingMatchReason,
  ListingMatchTier,
  RealEstateContact,
  REST_schema
} from "../../../../@types/sharedSchema";
import {
  getContactCrmIsHot,
  getContactCrmStatus,
  getContactCrmSubStatus,
  getContactCustomerTypes
} from "../../../../@types/sharedSchema";
import type { CrmContactRow } from "../../../../@types/webotServer";
// Plain TS with no imports of its own, so the pure model stays React-free.
import { relativeHebrew } from "../../crm/crmFormat";
// Pure as well (the book-key rules).
import { isOrgBookKey } from "../../../../hooks/contacts/contactBookRules";

/**
 * - `own`      — the viewer's own contact. Every action.
 * - `full`     — a colleague's contact the viewer may browse (agency `browse` permission):
 *                identity visible, selectable, sendable.
 * - `redacted` — a colleague's contact the viewer may NOT browse: no name, no phone. Shown so
 *                the realtor knows a colleague has a buyer (and whom to call), never selectable.
 */
export type MatchRowAccess = "own" | "full" | "redacted";

export type MatchRow = {
  key: string;
  /** E.164 — present exactly when the identity is visible. */
  contactId?: string;
  /**
   * The BOOK the row's record is in: the viewer for an own personal row, the colleague for a
   * colleague's, and for a pooled office's one record the office's `org:` key, whoever's list
   * it is on (`isOfficeRecordRow`) — the book a panel opens and a send is recorded against.
   */
  ownerId: string;
  ownerName?: string;
  ownerPhone?: string;
  ownerAvatar?: string;
  /** For a colleague's row: the shared org that grants access (the `orgId` of agency-scoped writes). */
  sharedOrgId?: string;
  /**
   * A pooled office's record that NOBODY has on their list: the office's own group (the server's
   * `realtorMail` is the office's `org:` key, 2026-10-22). Its owner is named "המשרד" (so a
   * redacted one reads "לקוח של המשרד") and its line says "לא אצל אף מתווך".
   */
  officeOnly?: true;
  access: MatchRowAccess;
  displayName: string;
  firstName?: string;
  /**
   * ⚠️ NOTHING ON THIS SCREEN READS THIS. It is carried so a row can be inspected and so the
   * shape matches the server, and that is all: the wording went on 2026-09-22 (realtors read a
   * thin contact's `full` as "fits best"), the ring's colour went to `SCORE_BANDS`, the filter
   * went to `countMatchQuality`, and the sort no longer breaks ties on it. Rendering or filtering
   * on it again reintroduces a second axis that disagrees with the percentage beside it.
   */
  tier?: ListingMatchTier;
  confidence: number;
  reasons: ListingMatchReason[];
  /** The contact card holds nothing beyond a budget and an area (the server's `dataDepth` reason). */
  needsMoreContactData: boolean;
  /**
   * A WIDE match: the listing contradicts something the customer asked for. Hidden unless the
   * realtor ticks "הצגת התאמות רחבות יותר", and never counted while it is hidden.
   */
  isLoose: boolean;
  budget?: number;
  isHot: boolean;
  /** The contact's CRM lifecycle status — on the payload since the first version, unrendered until 2026-09-30. */
  crmStatus: ReturnType<typeof getContactCrmStatus>;
  /** ISO, or null when the payload does not carry it (an older server, a stored snapshot, a redacted row). */
  createdTime: string | null;
  updatedTime: string | null;
  /** The row as the server sent it — what the bulk-message dialog and the row actions read. */
  contact: Partial<REST_schema.RealEstateContactWithConfidence>;
};

export type MatchScope = "all" | "mine" | "org";

/*
 * ⚠️ THERE IS NO QUALITY FILTER. DO NOT ADD ONE BACK.
 *
 * `top` (nothing the customer asked for is broken, and the score reached the green band) and `near`
 * (THE REST, wide matches included) are now two SECTIONS OF ONE LIST with a heading between them —
 * see `partitionMatchRowsByQuality` and the separator in `ListingMatchesTable`.
 *
 * This control was rebuilt four times and every version failed in a way the next one inherited:
 *
 *   1. Three controls at once — an all/top toggle, a "הצגת התאמות רחבות יותר" checkbox and a
 *      "בחירת כל ההתאמות מעל X%" box — for what is one question.
 *   2. A three-rung CUMULATIVE ladder: on a listing whose every match was top, two rungs showed the
 *      identical rows ("the content of both tabs is the same... wtf?"), and the counts did not add
 *      up (3+ / 1+).
 *   3. Two DISJOINT tabs: countable at last, but a realtor on the default rung could not see that
 *      anything else existed without pressing something, so a button had to be bolted under the
 *      list to say so — which is a navigation control apologising for a navigation control.
 *   4. Hiding the empty tab, twice. Rejected both times: the toolbar moves as a listing's matches
 *      change, and "0" is an answer where a missing control is not.
 *
 * A heading inside the list answers all four at once. Everything is on screen, in score order, and
 * the line between the groups is a label the realtor reads rather than a state she has to discover.
 * Nothing is hidden, so nothing has to be offered.
 */

const nameOf = (contact: Partial<REST_schema.RealEstateContactWithConfidence>): string => {
  const parts = [contact.callerFirstName, contact.callerLastName].map((part) => (part ?? "").trim()).filter(Boolean);
  if (parts.length) return parts.join(" ");
  return (contact.contactFullName ?? "").trim();
};

const budgetOf = (
  contact: Partial<REST_schema.RealEstateContactWithConfidence>,
  listedFor: ListingListedFor
): number | undefined => {
  const raw = listedFor === "rent" ? contact.customerBudgetRent : contact.customerBudget;
  return typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? raw : undefined;
};

/**
 * `createdTime`/`updatedTime` are typed `Date` on the shared contact type — it is the FIRESTORE
 * document's type, and `sharedSchema.ts` is mirrored with the server and forbids client-only
 * divergences, so the type cannot be "fixed" here. Over the wire they arrive as whatever JSON could
 * carry: an ISO string from `JSON.stringify(Date)`, an epoch number, or a Firestore Timestamp's
 * `{_seconds}` shape if one ever reaches the response un-converted.
 *
 * Everything that renders a time on this screen goes through here, so a shape change is one edit
 * rather than five. Anything unrecognised → `null`: a fabricated date under "נוצר לפני…" is worse
 * than no line at all.
 */
export const matchTimeIso = (value: unknown): string | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.toISOString() : null;
  if (typeof value === "number") return Number.isFinite(value) ? new Date(value).toISOString() : null;
  if (typeof value === "string") return Number.isFinite(new Date(value).getTime()) ? value : null;
  const seconds =
    (value as { _seconds?: number; seconds?: number })._seconds ?? (value as { seconds?: number }).seconds;
  return typeof seconds === "number" && Number.isFinite(seconds) ? new Date(seconds * 1000).toISOString() : null;
};

const baseRow = (
  contact: Partial<REST_schema.RealEstateContactWithConfidence>,
  listedFor: ListingListedFor
): Pick<
  MatchRow,
  | "tier"
  | "confidence"
  | "reasons"
  | "needsMoreContactData"
  | "isLoose"
  | "budget"
  | "isHot"
  | "crmStatus"
  | "createdTime"
  | "updatedTime"
  | "contact"
> => ({
  tier: contact.matchTier,
  confidence: typeof contact.confidencePercent === "number" ? contact.confidencePercent : 0,
  reasons: contact.matchReasons ?? [],
  needsMoreContactData: (contact.matchReasons ?? []).some((reason) => reason.criterion === "dataDepth"),
  // The server's flag is the source of truth; the reason scan covers a payload from before it
  // existed (a stored snapshot, an older server) so such a row is hidden rather than passed off
  // as a normal match.
  isLoose: contact.isLooseMatch === true || (contact.matchReasons ?? []).some((reason) => reason.status === "mismatch"),
  budget: budgetOf(contact, listedFor),
  isHot: Boolean(getContactCrmIsHot(contact)),
  crmStatus: getContactCrmStatus(contact as RealEstateContact),
  createdTime: matchTimeIso(contact.createdTime),
  updatedTime: matchTimeIso(contact.updatedTime),
  contact
});

/**
 * The pooled office a matched contact's record belongs to (its `org:` book, plan D4), or null
 * for a personal row and for a payload that carries no book (an older server or snapshot).
 */
const officeBookOf = (contact: Partial<REST_schema.RealEstateContactWithConfidence>): string | null =>
  contact.bookKey && isOrgBookKey(contact.bookKey) ? contact.bookKey : null;

/**
 * A POOLED OFFICE'S ONE RECORD (plan D4): the viewer's own book, whoever's list it is on. Opened,
 * edited and recorded against as the office's (`org:` key), never as a colleague's personal
 * contact: the server groups office rows by the colleagues who HOLD them, so the same record can
 * arrive as "mine" and under one or more colleagues (`buildMatchRows` keeps it once).
 */
export const isOfficeRecordRow = (row: Pick<MatchRow, "ownerId">): boolean => isOrgBookKey(row.ownerId);

export function buildMatchRows(args: {
  myContacts?: Partial<REST_schema.RealEstateContactWithConfidence>[];
  orgGroups?: REST_schema.RealtorsWithContactsForListing[];
  viewerId: string;
  listedFor: ListingListedFor;
}): MatchRow[] {
  const { myContacts = [], orgGroups = [], viewerId, listedFor } = args;
  const rows: MatchRow[] = [];
  // A pooled office's records already on the table. The server offers one under every colleague
  // who holds it (and as "mine" when the viewer does); it is one person and one record, shown
  // once: the viewer's own row first, else the first colleague group's.
  const officeRecordsShown = new Set<string>();

  myContacts.forEach((contact, index) => {
    const name = nameOf(contact);
    const office = officeBookOf(contact);
    if (office && contact.id) officeRecordsShown.add(contact.id);
    rows.push({
      ...baseRow(contact, listedFor),
      key: `mine|${contact.id ?? index}`,
      contactId: contact.id ?? undefined,
      ownerId: office ?? viewerId,
      access: "own",
      displayName: name || contact.id || "-",
      firstName: (contact.callerFirstName ?? name.split(" ")[0] ?? "").trim() || undefined
    });
  });

  for (const group of orgGroups) {
    const access: MatchRowAccess = group.contactAccess === "full" ? "full" : "redacted";
    // The office's own group — its records nobody holds — is keyed by the office's book, not a person.
    const officeOnly = isOrgBookKey(group.realtorMail);
    const ownerName = officeOnly ? "המשרד" : (group.realtorName ?? "").trim() || group.realtorMail;
    group.partialContactDetails.forEach((contact, index) => {
      const name = nameOf(contact);
      const visibleId = access === "full" ? contact.id ?? undefined : undefined;
      const office = officeBookOf(contact);
      if (office && visibleId) {
        if (officeRecordsShown.has(visibleId)) return;
        officeRecordsShown.add(visibleId);
      }
      rows.push({
        ...baseRow(contact, listedFor),
        key: `org|${group.realtorMail}|${visibleId ?? index}`,
        contactId: visibleId,
        ownerId: office ?? group.realtorMail,
        ownerName,
        ownerPhone: group.realtorPhoneNumberInternational,
        ownerAvatar: group.realtorAvatar,
        sharedOrgId: group.sharedOrgId,
        ...(officeOnly ? { officeOnly: true as const } : {}),
        access,
        displayName: access === "full" ? name || visibleId || "-" : `לקוח של ${ownerName}`,
        firstName: access === "full" ? (contact.callerFirstName ?? name.split(" ")[0] ?? "").trim() || undefined : undefined
      });
    });
  }

  return rows;
}

/** A row the bulk actions can act on: a visible identity, and not a redacted colleague row. */
export const isRowSelectable = (row: MatchRow): boolean => row.access !== "redacted" && Boolean(row.contactId);

/**
 * The score ring's colour, from FIXED BANDS on the number itself — never from the tier.
 *
 * Tier-coloured rings were half of the 2026-09-22 complaint (reported with a screenshot): a 42%
 * ring rendered GREEN sitting directly above a 50% ring rendered AMBER, because the colour came
 * from `full`/`partial` while the number came from the score. Measured over 9,723 production
 * matches at the time: **3,436 of 3,437 "full" matches scored below the best "partial"** — two
 * channels giving two different answers on one row.
 *
 * One channel now: the colour is a function of the number inside it, so they cannot disagree.
 *
 * FIXED, not relative to the result set. A realtor comparing two listings has to be able to read
 * "green" the same way on both, and a per-list scale would paint the best of a bad set excellent.
 *
 * The thresholds are cut from the rebuilt score's measured distribution, never picked (server:
 * `computeListingMatchScore`; `adHocOperations/measure-listing-match-scores.ts`). Over 10,657
 * production matches on 2026-09-23: min 34, p25 49, median 58, p90 75, max 100 — so **14% of
 * matches are green, 44% amber, 42% grey**. A complete card whose type, budget and rooms all
 * match reads 80 at neighbourhood level; a half-filled one cannot reach green, by design.
 *
 * ⚠️ CROSS-REPO PIN (2026-09-30). The server applies TWO ceilings, and both are pinned to this
 * number minus one (`listingMatchRules.ts`):
 *   · `VIOLATION_SCORE_CEILING[1]` = 74 — one requirement the listing BREAKS.
 *   · `CARD_DEPTH_SCORE_CEILING[3]` = 74 — the contact stated only three criteria.
 * So **green means one thing: nothing the customer asked for is broken, AND they told us enough to
 * judge** — a theorem, not an observation. The two repos must move together, and
 * `scoreBand(74).color === "warning"` in matchRows.test.ts is the assertion that catches it.
 *
 * So if green ever looks too scarce, lower the server's CEILINGS (one file), never these bands
 * (three surfaces across two repos) — and re-measure FIRST with
 * `adHocOperations/measure-listing-match-scores.ts --compare`. The bands are only meaningful
 * against a distribution: cut against the previous one (median 68) they put just 3.8% of rows in
 * green.
 */
export const SCORE_BANDS: ReadonlyArray<{ min: number; color: "success" | "warning" | "default"; label: string }> = [
  { min: 75, color: "success", label: "התאמה גבוהה" },
  { min: 55, color: "warning", label: "התאמה בינונית" },
  { min: 0, color: "default", label: "התאמה נמוכה" }
];

export const scoreBand = (score: number): typeof SCORE_BANDS[number] =>
  SCORE_BANDS.find((band) => score >= band.min) ?? SCORE_BANDS[SCORE_BANDS.length - 1];

/**
 * ONE recipient per phone. The matcher does not dedupe across books, so the same buyer can be the
 * viewer's own row AND a colleague's full-access row (or sit in two colleagues' books); selecting
 * both would send every bulk action to that phone twice. The viewer's own copy wins — it is the one
 * whose record a send is logged against.
 */
export function uniqueRecipientRows(rows: MatchRow[]): MatchRow[] {
  const byPhone = new Map<string, MatchRow>();
  const colleagueFlags = new Map<string, { optedOut: boolean; noNumber: boolean; spam: boolean }>();
  for (const row of rows) {
    if (!row.contactId) continue;
    if (row.access !== "own") {
      const flags = colleagueFlags.get(row.contactId) ?? { optedOut: false, noNumber: false, spam: false };
      colleagueFlags.set(row.contactId, {
        optedOut: flags.optedOut || row.contact.optedOutOfBulkMessages === true,
        noNumber: flags.noNumber || row.contact.bulkMessagingNumberDoesntExists === true,
        spam: flags.spam || row.contact.isInSpamDatabase === true
      });
    }
    const current = byPhone.get(row.contactId);
    if (!current || (current.access !== "own" && row.access === "own")) byPhone.set(row.contactId, row);
  }
  // A phone only COLLEAGUES hold keeps every colleague's send-safety verdict, not just the kept
  // copy's: a buyer who opted out with one colleague must not be reached through another's row.
  // Which row survived used to decide that, i.e. the order the server listed the books in. (A phone
  // the viewer owns keeps the viewer's own flags — the ones the send gates read.)
  return [...byPhone.values()].map((row) => {
    const flags = row.access !== "own" && row.contactId ? colleagueFlags.get(row.contactId) : undefined;
    if (!flags || (!flags.optedOut && !flags.noNumber && !flags.spam)) return row;
    return {
      ...row,
      contact: {
        ...row.contact,
        ...(flags.optedOut ? { optedOutOfBulkMessages: true } : {}),
        ...(flags.noNumber ? { bulkMessagingNumberDoesntExists: true } : {}),
        ...(flags.spam ? { isInSpamDatabase: true } : {})
      }
    };
  });
}

/**
 * The rows for one scope, in display order: by match % DESCENDING, in every scope and under every
 * filter (product decision 2026-09-18 — "ordered by % in all tabs"). Equal scores keep the
 * server's own order (location precision, 🔥). Stable, so the "all" scope interleaves colleagues'
 * rows by score without reshuffling equals.
 */
export function rowsForScope(rows: MatchRow[], scope: MatchScope): MatchRow[] {
  const scoped = scope === "all" ? rows : rows.filter((row) => (scope === "mine" ? row.access === "own" : row.access !== "own"));
  return scoped
    .map((row, index) => ({ row, index }))
    .sort((a, b) => b.row.confidence - a.row.confidence || a.index - b.index)
    .map(({ row }) => row);
}

/**
 * The floor of the green band — a row the realtor should look at first. ONE definition: the ring is
 * green at or above it, and the "פחות מדויק" heading falls exactly where it stops. One number, so
 * the colour and the heading cannot disagree on screen — when the ring cut on the score and the
 * filter cut on the tier, a green 85% row fell outside the filter.
 *
 * The "בחירת כל ההתאמות מעל X%" box that also read this was deleted on 2026-09-30.
 */
export const TOP_MATCH_MIN_SCORE = SCORE_BANDS[0].min;

export const countLooseRows = (rows: MatchRow[]): number => rows.filter((row) => row.isLoose).length;

/** One definition, used by both the split and the counts so they cannot disagree. */
const isTopRow = (row: MatchRow): boolean => !row.isLoose && row.confidence >= TOP_MATCH_MIN_SCORE;

/**
 * ONE LIST, SPLIT BY A HEADING — not a filter (2026-10-01).
 *
 * ⚠️ SPLIT ON THE SCORE, never on the tier. The cut was once `ללא חוסרים` — the `full`/`partial`
 * tier under a neutral name — and it produced exactly the contradiction it was supposed to avoid:
 * an 85% row could sit outside it because one criterion the contact stated was merely *unknown* on
 * the listing (reported 2026-09-24, "it doesn't make sense to show a match with 85% but not show
 * it in ללא חוסרים"). A realtor reads the list by the percentage, so the split has to cut on the
 * percentage. The tier survives only as the server-side `dataDepth` gate; nothing renders it.
 *
 * `top` and `near` are DISJOINT and together are the whole list, so the heading's count and the
 * rows under it can never disagree. Each group keeps the order it arrived in (`rowsForScope` has
 * already sorted by confidence), so the split never reorders anything — it only says where the
 * line falls.
 *
 * WIDE rows (the listing contradicts something the customer asked for) are always `near`, whatever
 * they score.
 */
export function partitionMatchRowsByQuality(rows: MatchRow[]): { top: MatchRow[]; near: MatchRow[] } {
  const top: MatchRow[] = [];
  const near: MatchRow[] = [];
  for (const row of rows) (isTopRow(row) ? top : near).push(row);
  return { top, near };
}

export type MatchQualityCounts = { top: number; near: number };

/** Disjoint: `top + near` is the whole list. */
export function countMatchQuality(rows: MatchRow[]): MatchQualityCounts {
  const top = rows.filter(isTopRow).length;
  return { top, near: rows.length - top };
}

/**
 * The row's one-line history — "נוצר לפני 3 ימים · עודכן אתמול".
 *
 * ONE line, not two: this panel is SCANNED, and a second caption costs ~16px on every row for the
 * secondary half of a secondary fact. Relative rather than exact because that is what a scannable
 * cell is for — the convention is written out at `crmFormat.ts:114-135` — and the EXACT dates are
 * one click away in the expanded panel, where they can actually be compared.
 *
 * COLLAPSES to one clause when the two stamps round to the same phrase. A contact created and never
 * touched since would otherwise read "נוצר לפני 3 ימים · עודכן לפני 3 ימים" — the same fact twice,
 * on the majority of rows, in a cell with room for one.
 */
export function matchTimesLine(row: Pick<MatchRow, "createdTime" | "updatedTime">): string {
  const created = relativeHebrew(row.createdTime);
  const updated = relativeHebrew(row.updatedTime);
  if (created && updated && created !== updated) return `נוצר ${created} · עודכן ${updated}`;
  if (created) return `נוצר ${created}`;
  return updated ? `עודכן ${updated}` : "";
}

const numberOrNull = (value: unknown): number | null => (typeof value === "number" && Number.isFinite(value) ? value : null);

/**
 * The CRM side panel's ROW for a match row — the same `CrmContactRow` shape /crm hands
 * `CrmContactPanel`, so the panel behaves exactly as it does there:
 *
 * - own row → `ownerUserId` is the viewer: the panel serves the viewer's own card, live.
 * - a colleague's FULL-access row → `ownerUserId`/`ownerName` are the colleague: the panel
 *   runs own-first like /crm's "של המשרד" tab — the viewer's OWN copy of the person when one
 *   exists ("מוצג הכרטיס שלך · שמור גם אצל …"), otherwise the viewer's timeline with the
 *   client only ("איש הקשר שמור אצל … ולא אצלך"). The colleague's document is never read.
 * - a REDACTED row → `null`: there is no identity to open.
 * - a pooled office's record (`isOfficeRecordRow`), held by the viewer or by a colleague →
 *   the OFFICE's: `ownerUserId` and `bookKey` are the office's key and `holderIds` its list,
 *   so the panel reads and edits the one record, and draws the "הלקוחות שלי" line from it.
 *
 * Header-only projection (name, owner, types, status, 🔥, budgets); the panel hydrates the
 * body itself. Everything the match payload does not carry is empty, never guessed.
 */
export function toCrmPanelRow(row: MatchRow): CrmContactRow | null {
  if (row.access === "redacted" || !row.contactId) return null;
  const contact = row.contact;
  const firstName = (contact.callerFirstName ?? "").trim() || null;
  const lastName = (contact.callerLastName ?? "").trim() || null;
  const name = [firstName, lastName].filter(Boolean).join(" ") || (contact.contactFullName ?? "").trim() || null;
  const office = isOfficeRecordRow(row);
  // An office record is the viewer's own book: no colleague's name on its header.
  const ownBook = row.access === "own" || office;
  return {
    ownerUserId: row.ownerId,
    bookKey: row.ownerId,
    holderIds: office ? [...(contact.holderIds ?? [])] : [],
    ownerName: ownBook ? null : row.ownerName ?? null,
    ownerAvatarUrl: ownBook ? null : row.ownerAvatar ?? null,
    contactId: row.contactId,
    name,
    firstName,
    lastName,
    additionalNotes: null,
    contactOrigin: null,
    crmStatus: row.crmStatus,
    crmSubStatus: getContactCrmSubStatus(contact as RealEstateContact),
    crmIsHot: row.isHot,
    customerSeriousness: null,
    customerTypes: getContactCustomerTypes(contact as RealEstateContact),
    tags: [],
    cities: [],
    neighborhoods: [],
    interestedAreas: [],
    interestedInLocations: [],
    propertyTypes: (contact.interestedInPropertyTypes ?? []) as string[],
    sellingListingIds: [],
    rentingOutListingIds: [],
    budget: numberOrNull(contact.customerBudget),
    budgetMin: numberOrNull(contact.customerBudgetMin),
    budgetRent: numberOrNull(contact.customerBudgetRent),
    roomsMin: numberOrNull(contact.roomsMin),
    roomsMax: numberOrNull(contact.roomsMax),
    optedOutOfBulkMessages: contact.optedOutOfBulkMessages === true,
    isInSpamDatabase: contact.isInSpamDatabase === true,
    // The match payload carries only the campaign rail's stamp; half an answer to "when was
    // this person last messaged" is not one, so it is empty rather than guessed.
    lastBulkMessageSentTime: null,
    // Carried by the match payload since 2026-09-30 (`listingMatchContactProjection.ts`), so these
    // are read rather than guessed — the "never guessed" contract above is unchanged. Still `null`
    // on a payload from an older server or a stored snapshot written before the projection widened.
    createdTime: row.createdTime,
    updatedTime: row.updatedTime
  };
}

/**
 * What the side panel reported about an OWN contact after an edit made in it (its
 * `onContactPatched` / `onContactRekeyed`), keyed by the phone the row was built with.
 */
export type MatchRowPatch = Partial<
  Pick<CrmContactRow, "contactId" | "name" | "firstName" | "lastName" | "crmIsHot" | "crmStatus" | "crmSubStatus" | "budget" | "budgetRent">
>;

/**
 * The row as it reads AFTER the panel's edit — so closing the panel does not show the old name,
 * 🔥 or budget next to a save that worked (the same reason /crm patches its row in place). A
 * re-key moves the row onto the new phone, so its actions stop targeting a dead id. Own rows,
 * and a pooled office's records (one record, whoever's list it is on), only: a colleague's
 * PERSONAL row opened own-first may show the VIEWER's copy, whose fields must never be written
 * onto the colleague's row.
 *
 * `row.contact` is patched too, so everything derived from it — the panel's own header
 * (`toCrmPanelRow`), the bulk dialog's template variables — reads the edit, not the payload. Its
 * `id` is left alone: it is the key the patch itself is stored under.
 */
export function applyMatchRowPatch(row: MatchRow, patch: MatchRowPatch | undefined, listedFor: ListingListedFor): MatchRow {
  if (!patch || (row.access !== "own" && !isOfficeRecordRow(row))) return row;
  const hasName = "name" in patch || "firstName" in patch || "lastName" in patch;
  const name = hasName ? (patch.name ?? [patch.firstName, patch.lastName].filter(Boolean).join(" ")).trim() : "";
  const rawBudget = listedFor === "rent" ? patch.budgetRent : patch.budget;
  const hasBudget = listedFor === "rent" ? "budgetRent" in patch : "budget" in patch;
  const contact = {
    ...row.contact,
    ...(hasName
      ? {
          callerFirstName: patch.firstName ?? undefined,
          callerLastName: patch.lastName ?? undefined,
          contactFullName: name || undefined
        }
      : {}),
    ...(typeof patch.crmIsHot === "boolean" ? { crmIsHot: patch.crmIsHot } : {}),
    // `CrmContactRow.crmStatus` is widened to `string` for the wire; the contact document's own
    // field is the union, and `getContactCrmStatus` validates on the way back out.
    ...("crmStatus" in patch ? { crmStatus: (patch.crmStatus ?? undefined) as RealEstateContact["crmStatus"] } : {}),
    ...("crmSubStatus" in patch ? { crmSubStatus: patch.crmSubStatus ?? undefined } : {}),
    ...("budget" in patch ? { customerBudget: patch.budget ?? undefined } : {}),
    ...("budgetRent" in patch ? { customerBudgetRent: patch.budgetRent ?? undefined } : {})
  };
  return {
    ...row,
    contact,
    ...(patch.contactId ? { contactId: patch.contactId } : {}),
    ...(hasName ? { displayName: name || patch.contactId || row.contactId || "-", firstName: patch.firstName ?? undefined } : {}),
    ...(typeof patch.crmIsHot === "boolean" ? { isHot: patch.crmIsHot } : {}),
    // The row renders the status chip, so a change made in the side panel has to reach it too.
    ...("crmStatus" in patch ? { crmStatus: (patch.crmStatus ?? null) as MatchRow["crmStatus"] } : {}),
    ...(hasBudget ? { budget: typeof rawBudget === "number" && rawBudget > 0 ? rawBudget : undefined } : {})
  };
}

/**
 * The audience members for the selected rows — what "הוספה לקהל" writes. Pass `recipientRows`
 * (`uniqueRecipientRows`: one row per phone, the viewer's own copy winning, a colleague-only phone
 * carrying EVERY colleague's opt-out), so an own contact is never stored under a colleague's
 * `ownerUserId` and the skip below is decided per person, whatever order the books came in.
 *
 * A COLLEAGUE's contact who opted out of bulk messages is left out and counted. Once a person is
 * in an audience, the send gate reads the SENDER's suppression rows and contact document — never
 * the colleague owner's personal opt-out — so putting them in would quietly undo it. Own rows are
 * kept: the viewer's own opt-out IS what the send gate checks.
 *
 * A pooled office's record is filed under the office's key (`ownerUserId: 'org:…'`), as the
 * server's own audience-from-selection files it: that is the book the send's outcome is recorded
 * against. Its flag mirrors the office's blocked-list row, so an opted-out one is left out too.
 */
export function toAudienceMembers(rows: MatchRow[]): {
  members: REST_schema.WhatsappGroupMemberInput[];
  skippedColleagueOptOuts: number;
} {
  const members: REST_schema.WhatsappGroupMemberInput[] = [];
  let skippedColleagueOptOuts = 0;
  for (const row of rows) {
    if (row.access === "redacted" || !row.contactId) continue;
    if (row.access !== "own" && row.contact.optedOutOfBulkMessages === true) {
      skippedColleagueOptOuts += 1;
      continue;
    }
    const clean = (value: string | null | undefined): string | undefined => (value ?? "").trim() || undefined;
    members.push({
      phone: row.contactId,
      firstName: clean(row.contact.callerFirstName),
      lastName: clean(row.contact.callerLastName),
      displayName: row.displayName && row.displayName !== row.contactId ? row.displayName : undefined,
      contactId: row.contactId,
      ownerUserId: row.ownerId
    });
  }
  return { members, skippedColleagueOptOuts };
}

/**
 * "קונים · הרצל 5, חיפה · 18/09" — the listing's address, dated (a realtor makes several). Without
 * an address: "קונים לנכס · 18/09 14:30".
 */
export function defaultMatchAudienceName(addressText: string, audience: "buyers" | "renters", now: Date): string {
  const pad = (value: number): string => String(value).padStart(2, "0");
  const noun = audience === "renters" ? "שוכרים" : "קונים";
  const day = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}`;
  const address = addressText.trim();
  return address ? `${noun} · ${address} · ${day}` : `${noun} לנכס · ${day} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/** Worst news first: a contradiction, then a tolerated miss, then an unknown, then what fits. */
const STATUS_ORDER: Record<ListingMatchReason["status"], number> = { mismatch: 0, partial: 1, unknown: 2, match: 3 };

/**
 * Features a buyer asked for whose absence is a DEAL-BREAKER question, not a detail: when the
 * listing doesn't say it has one, the realtor must see it before anything else (product decision
 * 2026-09-18 — "when the contact wants a ממ"ד and there is none marked, the chip should be red").
 */
const CRITICAL_FEATURES: ReadonlySet<string> = new Set(["hasProtectedSpace"]);

/**
 * The buyer asked for a critical feature and the listing doesn't mark it. `featureKey` is the
 * source of truth; the label test covers a payload from before the server sent it (a stored
 * snapshot, an older server).
 */
export const isCriticalMissingFeature = (reason: ListingMatchReason): boolean =>
  reason.criterion === "feature" &&
  reason.status !== "match" &&
  (reason.featureKey ? CRITICAL_FEATURES.has(reason.featureKey) : reason.label.startsWith('ממ"ד'));

/** What is off first — a missing critical feature before anything — then what fits. */
export const orderMatchReasons = (reasons: ReadonlyArray<ListingMatchReason> | undefined): ListingMatchReason[] =>
  [...(reasons ?? [])].sort(
    (a, b) =>
      Number(isCriticalMissingFeature(b)) - Number(isCriticalMissingFeature(a)) || STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
  );

/**
 * The reasons as the row's "פרטים נוספים" panel reads them — three answers to three questions,
 * rather than one wall of chips.
 *
 * The chips were replaced 2026-09-22: up to nine of them per row, in four colours, with a "+N"
 * tooltip for the rest ("all the chips are too much"). A realtor opening a match wants to know
 * what is MISSING or DIFFERENT from what the customer asked for — which is one short list, not a
 * restatement of the whole card.
 *
 * `dataDepth` is pulled out of the lists entirely: it is a statement about the contact card, not
 * a criterion that was checked, and it is rendered as the panel's warning.
 */
export type MatchReasonGroups = {
  /**
   * The customer asked for it and the listing does not deliver it — whether because the listing
   * says it HASN'T got it (`mismatch`) or because the listing never says (`unknown`).
   *
   * ⚠️ THESE TWO WERE SEPARATE SECTIONS ("לא מתקיים בנכס" / "לא מצוין בנכס") AND ARE NOW ONE.
   * Reported 2026-09-30 with two screenshots of the SAME listing: one buyer's ממ"ד showed as
   * `ממ"ד / ממ"ק: לא צוין` in the grey section, another's as `אין ממ"ד` in the red one, and the two
   * read as two different things when the realtor's question is identical — is there a ממ"ד here or
   * not? The distinction is real to the data and worth keeping in the SCORE (a listing that says no
   * is worse news than one that is silent), but as two headings it asked the realtor to care about
   * where our information came from. One section, "חסר בנכס", answers their actual question; the
   * individual bullets still carry the precise wording.
   */
  missing: ListingMatchReason[];
  /** Fits what the customer asked for. */
  fits: ListingMatchReason[];
  /** Off by a tolerated amount — a type upgrade, a room out, over budget. */
  differs: ListingMatchReason[];
  /** Present ⇔ the card holds nothing beyond a budget and an area. */
  dataDepth?: ListingMatchReason;
};

export function groupMatchReasons(reasons: ReadonlyArray<ListingMatchReason> | undefined): MatchReasonGroups {
  const groups: MatchReasonGroups = { fits: [], differs: [], missing: [] };
  for (const reason of orderMatchReasons(reasons)) {
    if (reason.criterion === "dataDepth") groups.dataDepth = reason;
    else if (reason.status === "match") groups.fits.push(reason);
    else if (reason.status === "partial") groups.differs.push(reason);
    // `mismatch` and `unknown` together — see MatchReasonGroups.missing.
    else groups.missing.push(reason);
  }
  return groups;
}

/**
 * The short name of one thing the listing does not deliver — "ממ"ד", "גינה", "3 חדרים".
 *
 * The server's labels are written for the expanded panel, where there is room to be explicit:
 * `גינה: לא צוין`, `אין ממ"ד`. On the row they have to fit two or three to a line beside a
 * name and a score, so the qualifier is stripped and the noun kept — the badge's own wording
 * ("חסר") already supplies it once for all of them.
 */
const missingReasonShortLabel = (reason: ListingMatchReason): string => {
  const label = reason.label ?? "";
  const beforeColon = label.split(":")[0].trim();
  return beforeColon.replace(/^אין\s+/, "").trim() || label;
};

/**
 * "חסר: ממ"ד, גינה" — what the customer asked for and this listing does not deliver, on the
 * COLLAPSED row.
 *
 * It used to be visible only behind "פרטים נוספים", so a realtor scanning the list could not see
 * the gaps at all — reported 2026-09-30 against a 89% row whose customer had asked for a garden the
 * listing never mentions. Two names then a count, because three Hebrew nouns plus a number is
 * already wider than the cell on a phone and the realtor only needs to know whether to look.
 *
 * Both flavours are counted: the listing saying it hasn't got it and the listing never saying. They
 * are one question to a realtor — is there a garden here? — which is why the panel merged them into
 * "חסר בנכס" too.
 */
export function matchMissingSummary(reasons: ReadonlyArray<ListingMatchReason> | undefined): string {
  const missing = groupMatchReasons(reasons).missing;
  if (!missing.length) return "";
  const names = missing.map(missingReasonShortLabel).filter(Boolean);
  if (!names.length) return "";
  if (names.length <= 2) return `חסר: ${names.join(", ")}`;
  return `חסר: ${names.slice(0, 2).join(", ")} ועוד ${names.length - 2}`;
}

/** How many things the realtor should look at before calling — what the row's button counts. */
export const countMatchGaps = (reasons: ReadonlyArray<ListingMatchReason> | undefined): number => {
  const groups = groupMatchReasons(reasons);
  return groups.differs.length + groups.missing.length;
};
